/**
 * 서명 안 이미지의 크기를 style 로 굳혀, 이미지가 안 떠도 레이아웃이 무너지지 않게 한다.
 *
 * <h3>왜 필요한가</h3>
 * <p>명함형 서명은 로고 폭이 곧 오른쪽 열의 폭이다. 그런데 로고 URL 이 죽거나(404),
 * 받는 쪽이 이미지를 차단하면 그 폭이 통째로 사라져 열이 좁아지고 주소가 여러 줄로 접힌다.
 * 실측 — 로고 216px 가 뜰 때 오른쪽 열 220px(주소 한 줄), 깨지면 160px(주소 3줄로 접힘).
 *
 * <h3>왜 width 속성만으로는 안 되나</h3>
 * <p>{@code <img width="216">} 가 이미 있는데도 깨지면 160px 로 줄어든다.
 * HTML 명세상 이미지 로드가 실패하고 {@code alt} 텍스트가 있으면 그 요소는
 * <b>비대체(non-replaced) 인라인 요소</b>로 취급되는데, width/height 는 비대체 인라인 요소에
 * 적용되지 않기 때문이다. {@code style="width:216px"} 를 넣어도 같은 이유로 무시된다.
 *
 * <p>그래서 {@code display:inline-block} 을 함께 넣는다. 비대체 요소라도 inline-block 이면
 * width/height 가 먹는다. 이걸 넣으면 깨진 로고와 정상 로고가 완전히 같은 폭을 차지한다.
 * {@code overflow:hidden} 은 alt 텍스트가 상자 밖으로 흘러 빨간 밑줄을 넘는 걸 막는다.
 *
 * <h3>적용 범위</h3>
 * <p>서명 HTML 에만 쓴다. 본문 이미지는 사용자가 에디터에서 직접 크기를 조절하는 대상이라
 * (CustomImage 가 이미 width/height 를 스키마로 관리) 여기서 건드리면 그 흐름과 충돌한다.
 *
 * <p>이미 style 에 지정된 속성은 덮지 않으므로 여러 번 통과시켜도 결과가 같다(멱등).
 *
 * @param {string} html 서명 HTML
 * @returns {string} img 크기가 style 로 굳은 HTML
 */
export function pinImageBoxes(html) {
    if (!html) return html;

    // DOMParser 로 파싱 — 살아있는 DOM(innerHTML)에 넣으면 죽은 URL 로도 요청이 나가고
    // onerror 가 실행될 수 있다. DOMParser 문서는 비활성이라 그런 일이 없다.
    const doc = new DOMParser().parseFromString(String(html), "text/html");
    const imgs = doc.body.querySelectorAll("img");
    if (imgs.length === 0) return html;

    let touched = false;
    imgs.forEach((img) => {
        const w = numericAttr(img, "width");
        const h = numericAttr(img, "height");
        if (w == null && h == null) return;   // 크기 선언이 없으면 굳힐 것도 없다

        let style = img.getAttribute("style") || "";
        if (w != null) style = addDecl(style, "width", `${w}px`);
        if (h != null) style = addDecl(style, "height", `${h}px`);
        style = addDecl(style, "display", "inline-block");
        style = addDecl(style, "overflow", "hidden");

        if (style !== (img.getAttribute("style") || "")) {
            img.setAttribute("style", style);
            touched = true;
        }
    });

    return touched ? doc.body.innerHTML : html;
}

/** width="216" 처럼 순수 숫자일 때만 px 로 굳힌다. "100%" 등 반응형 값은 그대로 둔다. */
function numericAttr(el, name) {
    const raw = (el.getAttribute(name) || "").trim();
    return /^\d{1,5}$/.test(raw) ? raw : null;
}

/** 이미 선언된 속성은 건드리지 않는다 — 사용자가 지정한 값이 항상 우선. */
function addDecl(style, prop, value) {
    // (^|;) 로 시작을 강제해 "max-width" 가 "width" 로 잘못 잡히지 않게 한다.
    if (new RegExp(`(^|;)\\s*${prop}\\s*:`, "i").test(style)) return style;
    let base = (style || "").trim();
    if (base && !base.endsWith(";")) base += ";";
    return (base ? base + " " : "") + `${prop}: ${value};`;
}
